import {Component, Input} from '@angular/core';
import {DbActiveModal} from "../../../modules/modal/modal-ref";
import {filter, first} from "rxjs/operators";
import {HostingService} from "../../../websites/website/hosting/hosting.service";
import {IWebsiteInstallationForm, IWebsiteInstallData, WebsiteInstallData} from "./models/website-install-data.model";
import {NotifierComponent} from "../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../shared/components/notifier/notification";
import {NotificationType} from "../../../shared/components/notifier/notification-type";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {Router} from "@angular/router";
import {DbModalService} from "../../../modules/modal/modal";
import {CreateWebsiteSuccessComponent} from "../create-website-success/create-website-success.component";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {getWebsite} from "../../../store";
import {IWebsiteInstallCloneForm, WebsiteInstallCloneData} from "./models/website-clone-data.model";
import {UserSubscription} from "../../../user/user-subscription";
import {
  ManualMigrationDialogComponent
} from "../../../shared/components/dialogs/migration/manual-migration-dialog/manual-migration-dialog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";

export type WebsiteInstallationStep = 'websiteInstallation' | 'creationProcess' | 'cloneProcess';

export interface CreateWebsiteChangeStepEvent {
  step: WebsiteInstallationStep;
  options?: {
    websiteData?: IWebsiteInstallationForm,
    navigateTo?: string;
  };
}

@Component({
    selector: 'db-create-website',
    templateUrl: './create-website.component.html',
    styleUrls: ['./create-website.component.scss'],
    standalone: false
})
export class CreateWebsiteComponent {

  public step: WebsiteInstallationStep = 'websiteInstallation';
  public creationProcessStep: string;
  cloneWebsiteUrl: string;
  dataCenter: string;

  private _websiteData: IWebsiteInstallData;

  @Input() userSubscription: UserSubscription;

  constructor(
    private _activeModal: DbActiveModal,
    private _modalService: DbModalService,
    private _hostingService: HostingService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _router: Router,
    private _store: Store<fromStore.DbStoreState>,
    private _dialogService: DialogService
  ) { }

  public resize(size: [number, number]): void {
    this._activeModal.resize(...size);
  }

  public createWebsite(websiteData: IWebsiteInstallationForm): void {
    this.step = 'creationProcess';
    this.resize([330, 700]);
    this._websiteData = this._websiteDataMap(websiteData);
    this._create();
  }

  private _create(): void {
    if (!this._websiteData) {
      this.step = 'websiteInstallation';
      return;
    }
    this._websiteData.domain_name && this._sendAddDomainEvent();

    this._hostingService.createWebsite(this._websiteData)
      .pipe(
        filter(response => !!response),
        first()
      )
      .subscribe(response => {
        if (!response.success) {
          this.step = 'websiteInstallation';
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        } else {
          this.creationProcessStep = 'done';
          /** 'done' step visualization p.s. only for UX */
          setTimeout(() => {
            this._showCreateWebsiteSuccess(response.data.domain_id, response.data.nameservers);
          }, 1000);
        }
      });
  }

  /** map new Data into old website creation data */
  private _websiteDataMap(websiteData: IWebsiteInstallationForm): IWebsiteInstallData {
    return new WebsiteInstallData(websiteData);
  }

  private _sendAddDomainEvent(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Create a Website',
      eventLabel: 'Add domain'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private _showCreateWebsiteSuccess(websiteId: number, nameservers: string): void {
    this._store.select(getWebsite(websiteId))
      .pipe(
        first(website => !!website)
      )
      .subscribe(website => {
        this._store.dispatch(new fromStore.LoadUser());
        this._activeModal.close();
        const modal = this._modalService.open(CreateWebsiteSuccessComponent);
        modal.componentInstance.website = website;
        if (nameservers) {
          modal.componentInstance.nameservers = JSON.parse(nameservers);
        }
      });
  }

  cloneWebsite(websiteData: IWebsiteInstallCloneForm): void {
    const cloneData = new WebsiteInstallCloneData(websiteData);
    this._hostingService.autoMigration(cloneData, +websiteData.websiteId)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (response.success) {
          this.cloneWebsiteUrl = websiteData.websiteUrl;
          this.dataCenter = websiteData.dataCenter;
          this.step = 'cloneProcess';
        }
      });
  }

  public showCreateWebsiteSuccessComponent(websiteId: number): void {
    this._store.select(fromStore.getWebsite(websiteId))
      .pipe(filter(website => !!website))
      .subscribe(website => {
        this._activeModal.close();
        const modal = this._modalService.open(CreateWebsiteSuccessComponent);
        modal.componentInstance.website = website;
        modal.componentInstance.from = 'clone';
      });
  }

  public showManualMigrationModal(): void {
    this._activeModal.close();
    this._dialogService.addDialog(ManualMigrationDialogComponent, {ErrMessage: true});
  }

}

